/**
 * The app's single connection to the API.
 *
 * `@powwow/api-client` carries the session cookie, the CSRF echo and the community header; the
 * typed Hono client is used where the route types resolve end to end, and `call` covers the rest.
 * Both go through `unwrap`, so a failure is always an `ApiRequestError` with its field errors
 * intact — which is what lets a form put each message beside the control it belongs to.
 */

import {
  ApiRequestError,
  CSRF_COOKIE,
  createClient,
  unwrap,
  withNetworkErrors,
} from "@powwow/api-client";
import { safeUrl } from "@powwow/validation";
import type { ExportType, FormDefinition, GrantScope, Permission } from "@powwow/validation";
import { API_BASE_URL } from "./api-base.js";
import { toWorkbook } from "./lib/xlsx.js";
import { stationBody } from "./lib/station.js";

export { ApiRequestError };

const BASE_URL = API_BASE_URL;

/**
 * Hand a blob to the browser as a file.
 *
 * Revoking the object URL matters and is the reason this is a function rather than four lines
 * repeated: an un-revoked URL keeps the whole file in memory for the life of the tab, and a
 * treasurer's tab is open all weekend across a few dozen exports. A minute is long enough for the
 * download to start and short enough that the memory comes back.
 */
function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function client(communityId?: string) {
  return createClient({ baseUrl: BASE_URL, communityId });
}

/**
 * A request, with the tenant header and the CSRF echo, returning the raw `Response`.
 *
 * Separate from `call` because one route does not answer in JSON: an export is a CSV with a
 * `Content-Disposition`, and running that through `unwrap` would parse the file as an object and
 * throw. A failure still comes back as an `ApiRequestError` with its fields intact, because the
 * error envelope is JSON whatever the success body is.
 */
async function raw(
  communityId: string | undefined,
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const headers = new Headers(init.headers);
  headers.set("content-type", "application/json");
  if (communityId) headers.set("x-powwow-community", communityId);

  const csrf = document.cookie
    .split(";")
    .map((part) => part.trim().split("="))
    .find(([key]) => key === CSRF_COOKIE)?.[1];
  if (csrf) headers.set("x-powwow-csrf", decodeURIComponent(csrf));

  const response = await withNetworkErrors(() =>
    fetch(`${BASE_URL}${path}`, { ...init, headers, credentials: "include" }),
  );
  if (!response.ok) await unwrap(response);
  return response;
}

async function call<T>(
  communityId: string | undefined,
  path: string,
  init: RequestInit = {},
): Promise<T> {
  return (await (await raw(communityId, path, init)).json()) as T;
}

/**
 * A multipart upload — the same headers as `raw`, minus the one it must not send.
 *
 * `raw` sets `content-type: application/json` for every request, which is right for all of them
 * except this: a multipart body's content type carries a generated boundary, and the only thing
 * that knows it is the browser. Setting the header by hand produces a body the server cannot split
 * and an error that reads like a corrupt file.
 */
async function upload<T>(
  communityId: string,
  path: string,
  body: FormData,
  method: "POST" | "PUT" = "POST",
): Promise<T> {
  const headers = new Headers({ "x-powwow-community": communityId });
  const csrf = document.cookie
    .split(";")
    .map((part) => part.trim().split("="))
    .find(([key]) => key === CSRF_COOKIE)?.[1];
  if (csrf) headers.set("x-powwow-csrf", decodeURIComponent(csrf));

  const response = await withNetworkErrors(() =>
    fetch(`${BASE_URL}${path}`, { method, headers, body, credentials: "include" }),
  );
  if (!response.ok) await unwrap(response);
  return (await response.json()) as T;
}

/* ------------------------------------------------------------------- auth */

export interface CommunityMembership {
  id: string;
  slug: string;
  name: string;
  timezone: string;
  permissions: string[];
  /**
   * The community's own mark, when it has set one. Optional rather than required because a
   * membership can also come from `sessionStorage` — a platform admin's impersonation (D48) —
   * where it may have been written by an older build.
   */
  logo?: string | null;
}

export interface User {
  id: string;
  name: string;
  email: string | null;
  preferredName?: string | null;
  phone?: string | null;
  locale?: string | null;
}

/** A union rather than an optional field: `user` exists exactly when `authenticated` is true. */
export type Me =
  | {
      authenticated: false;
      communities: never[];
      platformAdmin: false;
      mustPasswordReset: false;
      hasPassword: false;
    }
  | {
      authenticated: true;
      user: User;
      communities: CommunityMembership[];
      /** Whether this person may reach the platform admin surface (create and list communities). */
      platformAdmin: boolean;
      /** Holding a password somebody else chose — they may do nothing else until they replace it. */
      mustPasswordReset: boolean;
      /** False for someone who has only ever arrived by link, so we can offer them a password. */
      hasPassword: boolean;
    };

export async function fetchMe(): Promise<Me> {
  const body = await unwrap<
    | { authenticated: false }
    | {
        authenticated: true;
        user: User;
        communities: CommunityMembership[];
        platformAdmin?: boolean;
        mustPasswordReset?: boolean;
        hasPassword?: boolean;
      }
  >(await client().v1.auth.me.$get());
  return body.authenticated
    ? {
        authenticated: true,
        user: body.user,
        communities: body.communities,
        platformAdmin: body.platformAdmin ?? false,
        mustPasswordReset: body.mustPasswordReset ?? false,
        hasPassword: body.hasPassword ?? false,
      }
    : {
        authenticated: false,
        communities: [],
        platformAdmin: false,
        mustPasswordReset: false,
        hasPassword: false,
      };
}

/* ------------------------------------------------------------------ passwords */

/** Sign in with an email address and a password. `mustReset` means: send them to choose one. */
export async function passwordSignIn(input: {
  email: string;
  password: string;
}): Promise<{ mustReset: boolean }> {
  const body = await call<{ ok: true; mustReset: boolean }>(
    undefined,
    "/v1/auth/password/sign-in",
    { method: "POST", body: JSON.stringify(input) },
  );
  return { mustReset: body.mustReset };
}

/**
 * Choose a password.
 *
 * `token` for a reset link, `currentPassword` for an ordinary change, and neither when the session
 * is already sitting in the forced-reset state — the server decides which of the three applies.
 */
export async function setPassword(input: {
  password: string;
  currentPassword?: string;
  token?: string;
}): Promise<void> {
  await call(undefined, "/v1/auth/password/set", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/** Ask for a reset link. Always succeeds, whether or not the address is known. */
export async function forgotPassword(email: string): Promise<void> {
  await call(undefined, "/v1/auth/password/forgot", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

/** Ask for a one-tap sign-in link by email. Always succeeds, for the same reason. */
export async function requestMagicLink(email: string): Promise<void> {
  await call(undefined, "/v1/auth/magic-link/request", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

/**
 * Accept a staff invitation, choose a password, and sign in.
 *
 * The token comes from the invite link (`/accept-invitation?token=…`). The server creates the
 * account, sets this password (unless the address already has one), and returns having issued a
 * session cookie — so the caller can go straight to `fetchMe` and into the app.
 */
export async function acceptInvitation(input: {
  token: string;
  name: string;
  password: string;
}): Promise<void> {
  await call(undefined, "/v1/auth/invitations/accept", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/* --------------------------------------------------------------- platform admin */

export interface PlatformCommunity {
  id: string;
  slug: string;
  name: string;
  status: string;
  /** Set when archived, so the list can distinguish it from a merely suspended community. */
  archivedAt: number | null;
  timezone: string;
  primaryHostname: string | null;
  memberCount: number;
  createdAt: number;
}

/** The full detail behind one community, for the edit view — attributes plus its current owner. */
export interface PlatformCommunityDetail {
  id: string;
  slug: string;
  name: string;
  legalName: string | null;
  publicName: string | null;
  shortName: string | null;
  description: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  mailingAddress: string | null;
  website: string | null;
  region: string | null;
  timezone: string;
  currency: string;
  status: string;
  archivedAt: number | null;
  primaryHostname: string | null;
  owner: { userId: string; name: string; email: string | null } | null;
  createdAt: number;
}

/** The attributes a platform admin may change. Absent = leave alone, null = clear. */
export interface UpdateCommunityInput {
  name?: string;
  legalName?: string | null;
  publicName?: string | null;
  shortName?: string | null;
  description?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  mailingAddress?: string | null;
  website?: string | null;
  region?: string | null;
  timezone?: string;
  currency?: string;
}

/** The verbs the console offers for a community's lifecycle. */
export type CommunityStatusAction = "deactivate" | "reactivate" | "archive" | "unarchive";

/** The result of transferring ownership. `signIn` is present only for a newly-created owner. */
export interface OwnerReassignment {
  userId: string;
  email: string;
  created: boolean;
  signIn: {
    signInToken: string;
    signInExpiresAt: number;
    temporaryPassword: string;
    /** Whether email is configured, i.e. whether this was also sent rather than only shown. */
    emailed: boolean;
  } | null;
}

/** What entering a community's workspace returns — its detail plus the owner permission keys. */
export interface ImpersonationGrant {
  community: PlatformCommunityDetail;
  ownerPermissions: string[];
}

export interface CreateCommunityInput {
  slug: string;
  name: string;
  legalName?: string | null;
  timezone: string;
  /** Province or territory code (e.g. "ON"). Omit when unset — the community can fill it in later. */
  region?: string;
  ownerName: string;
  ownerEmail: string;
}

export interface CreatedCommunity {
  community: { id: string; slug: string; hostname: string };
  owner: {
    userId: string;
    email: string;
    created: boolean;
    signInToken: string;
    signInExpiresAt: number;
    /** Their durable credential, shown once. They are made to replace it on first use. */
    temporaryPassword: string;
    /** Whether email is configured, i.e. whether this was also sent rather than only shown. */
    emailed: boolean;
  };
}

export async function adminListCommunities(): Promise<PlatformCommunity[]> {
  const body = await call<{ communities: PlatformCommunity[] }>(undefined, "/v1/admin/communities");
  return body.communities;
}

export async function adminCreateCommunity(input: CreateCommunityInput): Promise<CreatedCommunity> {
  return call<CreatedCommunity>(undefined, "/v1/admin/communities", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function adminGetCommunity(id: string): Promise<PlatformCommunityDetail> {
  const body = await call<{ community: PlatformCommunityDetail }>(
    undefined,
    `/v1/admin/communities/${id}`,
  );
  return body.community;
}

export async function adminUpdateCommunity(
  id: string,
  input: UpdateCommunityInput,
): Promise<PlatformCommunityDetail> {
  const body = await call<{ community: PlatformCommunityDetail }>(
    undefined,
    `/v1/admin/communities/${id}`,
    { method: "PATCH", body: JSON.stringify(input) },
  );
  return body.community;
}

export async function adminSetCommunityStatus(
  id: string,
  action: CommunityStatusAction,
): Promise<PlatformCommunityDetail> {
  const body = await call<{ community: PlatformCommunityDetail }>(
    undefined,
    `/v1/admin/communities/${id}/${action}`,
    { method: "POST" },
  );
  return body.community;
}

export async function adminReassignOwner(
  id: string,
  input: { ownerName: string; ownerEmail: string },
): Promise<OwnerReassignment> {
  const body = await call<{ owner: OwnerReassignment }>(
    undefined,
    `/v1/admin/communities/${id}/owner`,
    { method: "POST", body: JSON.stringify(input) },
  );
  return body.owner;
}

export async function adminBeginImpersonation(id: string): Promise<ImpersonationGrant> {
  return call<ImpersonationGrant>(undefined, `/v1/admin/communities/${id}/impersonate`, {
    method: "POST",
  });
}

export async function devSignIn(email: string): Promise<void> {
  await unwrap(await client()["v1"].auth["dev-sign-in"].$post({ json: { email } }));
}

/**
 * Exchange a magic-link token for a session.
 *
 * The real sign-in for anyone who is not a seeded dev account — the superadmin created by the
 * platform seed, and every community owner minted by the admin console. Single use: the server
 * revokes the token as it is spent, so a link that has already signed someone in opens nothing.
 */
export async function consumeMagicLink(token: string): Promise<void> {
  await unwrap(await client()["v1"].auth["magic-link"].consume.$post({ json: { token } }));
}

export async function signOut(): Promise<void> {
  await unwrap(await client()["v1"].auth["sign-out"].$post());
}

export function can(membership: CommunityMembership | null, permission: Permission): boolean {
  return membership?.permissions.includes(permission) ?? false;
}

/* ------------------------------------------------------------------ types */

/** The prose blocks of the public event page — `eventInfoSchema` in @powwow/validation. */
export interface EventInfo {
  registrationInstructions?: string;
  generalRules?: string;
  cancellation?: string;
  accessibility?: string;
  parking?: string;
  travel?: string;
  accommodation?: string;
  emergency?: string;
}

/**
 * An event, as the API returns it from both the list and the detail route.
 *
 * One shape rather than a summary and a detail: the list route selects the whole row, and keeping
 * two types in step by hand is how a field ends up rendered on one screen and missing on the other.
 */
/**
 * How judges get in, and for how long (D15, D79).
 *
 * Every field is optional because the stored blob is whatever an organiser last saved; the server
 * fills the gaps with its own defaults. Anything sent must therefore carry the fields it is *not*
 * changing, or saving one setting silently resets the others.
 */
export interface JudgeAccessPolicySettings {
  allowAccessCodes?: boolean;
  allowInvitations?: boolean;
  accessCodeLength?: number;
  accessCodeGraceMinutes?: number;
  accessCodeMaxHours?: number;
}

export interface EventSummary {
  id: string;
  slug: string;
  name: string;
  publicTitle: string | null;
  startsOn: string;
  endsOn: string;
  venue: string | null;
  venueAddress: string | null;
  city: string | null;
  region: string | null;
  postalCode: string | null;
  country: string | null;
  mapUrl: string | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  timezone: string;
  description: string | null;
  summary: string | null;
  visibility: "public" | "private";
  currency: string;
  /** The house target panel size for judge assignment (D56); a competition overrides it. */
  defaultPanelSize: number;
  /** How judges get in, and for how long (D15, D79). Absent until an organiser changes it. */
  judgeAccessPolicyJson: JudgeAccessPolicySettings | null;
  /** What one act of checking in covers, and whether a participant may record their own (D66). */
  checkInUnit: "session" | "competition";
  selfCheckIn: boolean;
  /** Whether arriving sends a confirmation email to whoever gave an address. */
  checkInEmail: boolean;
  infoJson: EventInfo | null;
  status: "draft" | "published" | "in_progress" | "complete" | "archived" | "cancelled";
}

/** What a duplicate carries over. Registrations, scores and payouts never can (see `clone.ts`). */
export interface DuplicateOptions {
  details: boolean;
  competitions: boolean;
  categories: boolean;
  phases: boolean;
  scoringConfigs: boolean;
  forms: boolean;
  prizeRules: boolean;
  announcements: boolean;
}

/** A role at an event: MC, arena director, host drum, elder, volunteer (D62). */
export interface EventRoleSummary {
  id: string;
  eventId: string | null;
  name: string;
  description: string | null;
  groupLabel: string | null;
  subjectKind: "person" | "group" | "either";
  minCount: number;
  /** Null is unbounded — head dancers, elders, flag carriers. */
  maxCount: number | null;
  expectsCheckin: boolean;
  publiclyListed: boolean;
  isTemplate: boolean;
  sortOrder: number;
  assignedCount: number;
}

export interface RoleAssignmentSummary {
  id: string;
  eventRoleId: string;
  roleName: string;
  participantId: string | null;
  teamId: string | null;
  userId: string | null;
  displayName: string;
  groupName: string | null;
  registrationId: string | null;
  agreedAmountCents: number | null;
  currency: string;
  status: "expected" | "confirmed" | "withdrawn";
  notes: string | null;
  sortOrder: number;
  /** Empty means every session (D62). */
  sessionIds: string[];
  /** False when the holder is a name on the poster with no person or group to pay. */
  payable: boolean;
}

export interface SessionSummary {
  id: string;
  name: string;
  date: string;
  grandEntryLocal: string | null;
  grandEntryAt: number | null;
  sortOrder: number;
}

export interface WindowSummary {
  id: string;
  /** Registration or check-in. Both are the same shape resolved by the same rule — the most
   *  specific wins — so they share a table and are told apart by this. */
  purpose: "registration" | "check_in";
  /** Empty means the window is event-wide. Naming sessions scopes it to those and only those (D49). */
  sessionIds: string[];
  opensAt: number | null;
  closesAt: number | null;
  status: "scheduled" | "open" | "closed";
  /** Empty means the window governs every competition. Naming some narrows it, and the narrower
   *  window overrides the broader one rather than joining it (D44). */
  competitionIds: string[];
}

export interface Finding {
  code: string;
  severity: "blocker" | "warning" | "info";
  message: string;
  action: string;
  link: string;
}

export interface Readiness {
  findings: Finding[];
  summary: { blockers: number; warnings: number; info: number; canPublish: boolean };
}

export interface Competition {
  id: string;
  name: string;
  type: "competitive" | "exhibition";
  format: "judged" | "placement_only" | "heat_or_bracket";
  location: string | null;
  /** When it starts, if not when its session does — the hand drum special at half five (D50). */
  startsLocal: string | null;
  /** The sessions it runs in (D50), its default schedule — categories and rounds may override (D53).
   *  Empty means it runs throughout / every session. */
  sessionIds: string[];
  /** How a dancer signs up across sessions (D53): one entry, or a separate one each session. */
  entryMode: "once" | "per_session";
  /** The participant unit (D55). Both 1 is individual; `rosterMax > 1` is a team competition. */
  rosterMin: number;
  rosterMax: number;
  /** Target panel size (D56), overriding the event default. Null falls back to it. Never a cap. */
  panelSize: number | null;
  /** How somebody comes to be in it (D75): chosen, automatic, or either. */
  enrolment: "chosen" | "automatic" | "either";
}

/**
 * Everything that runs in an event, flat.
 *
 * Flat because the shape a screen wants is by session, and turning this into that is
 * `sessionProgramme` in `@powwow/domain` — the same function the D53 rules elsewhere are built
 * from. A payload pre-grouped by the server would be a second answer to a question already
 * answered, and the two would drift.
 */
export interface EventSchedule {
  competitions: Competition[];
  categories: Array<{
    id: string;
    competitionId: string;
    name: string;
    /** The session it runs in (D53), or null to follow the competition. */
    sessionId: string | null;
    status: "draft" | "open" | "closed" | "complete";
  }>;
  phases: Array<{
    id: string;
    competitionId: string;
    name: string;
    /** The session this round runs in (D53), or null to follow the category or competition. */
    sessionId: string | null;
  }>;
}

export interface ScoringConfigDetail {
  id: string;
  method: string;
  version: number;
  tieBreaks: unknown[];
  options: Record<string, unknown>;
  criteria: Array<{
    id: string;
    name: string;
    minValue: number;
    maxValue: number;
    weightBp: number;
    step: number;
    description: string | null;
  }>;
}

export interface Category {
  id: string;
  competitionId: string;
  name: string;
  status: "draft" | "open" | "closed" | "complete";
  /** False for a category somebody typed. Generation never touches those (D43). */
  generated: boolean;
  entryLimit: number | null;
  /** The session it runs in (D53), or null to follow the competition. */
  sessionId: string | null;
  sortOrder: number;
  /** Dimension id → the value ids it stands for; an empty array is a collapsed dimension. One value
   *  per dimension for a generated category, possibly several for a hand-written one (D51). */
  assignments: Record<string, string[]>;
}

export interface ProposedCategory {
  name: string;
  assignments: Record<string, string[]>;
  sortOrder: number;
}

export interface CategoryPreview {
  proposed: ProposedCategory[];
  existing: Array<{ id: string; name: string }>;
  /** Hand-written categories, shown beside the grid so it is not mistaken for the whole programme. */
  custom: Category[];
  summary: {
    withoutCollapseRules: number;
    proposedCount: number;
    toCreate: number;
    toKeep: number;
    toArchive: number;
    customCount: number;
  };
}

export interface Participant {
  id: string;
  firstName: string;
  lastName: string;
  communityAffiliation: string | null;
  phone: string | null;
  /** Optional throughout: every contact on the pilot poster is a phone number (D21). */
  email?: string | null;
}

export interface RegistrationDetail {
  id: string;
  registrationNumber: string;
  dancerNumber: string | null;
  /** Completed years, as given for this event. On the registration, never on the person. */
  age: number | null;
  channel: "self" | "staff";
  registeredAt: number;
  participant: Participant;
  entries: Array<{
    id: string;
    competitionId: string;
    categoryId: string | null;
    sessionId: string | null;
    status: "entered" | "withdrawn" | "no_show";
  }>;
}

export interface LookupEntry {
  id: string;
  competitionId: string;
  categoryId: string | null;
  participantName: string;
  registrationNumber: string;
  dancerNumber: string | null;
}

export interface RosterEntry {
  id: string;
  userId: string;
  displayName: string;
  lastAssignedAt: number | null;
}

export interface Assignment {
  id: string;
  sessionId: string;
  competitionId: string;
  categoryId: string | null;
  userId: string;
  displayName: string;
  status: "assigned" | "active" | "completed" | "revoked";
  /**
   * When this judge's access code stops working, or null if they hold none.
   *
   * The code itself is hashed and gone; this is the one fact about it worth reading back, because
   * a judge turned away mid-afternoon costs a card re-entered from memory.
   */
  codeExpiresAt: number | null;
}

export interface ResultSet {
  id: string;
  categoryId: string | null;
  status: "draft" | "approved" | "published" | "superseded";
  computedAt: number;
  engineVersion: string;
}

export interface Placing {
  entryId: string;
  rank: number;
  total: number;
  tieBreakApplied?: string;
  tiedUnresolved?: boolean;
}

export interface Warning {
  code: string;
  message: string;
  entryId?: string;
  /** Present on the warnings that name a judge — a missing card, a duplicate score. */
  judgeId?: string;
}

export interface Payout {
  id: string;
  participantId: string;
  sessionId: string | null;
  amountCents: number;
  currency: string;
  status: "calculated" | "approved" | "paid" | "void";
  explanationJson: {
    text?: string;
    rule?: string;
    participant?: string;
    registrationNumber?: string | null;
    dancerNumber?: string | null;
    /**
     * Present when a person set this amount rather than a rule.
     *
     * Written onto the line itself, not left to the audit log, because "why is this not what the
     * rule says" has to be answerable on the payout sheet and in the export — the two places the
     * question is actually asked.
     */
    manualOverride?: {
      reason: string;
      previousAmountCents: number;
      supersedes: string;
      at: number;
      by: string | null;
    };
  } | null;
}

/**
 * Everything a printed slip says, assembled by the server (D85).
 *
 * The desk builds the same shape locally from the tiles it just drew, because a round trip in the
 * middle of a queue is a round trip nobody asked for. Every other place a slip is printed from —
 * a participant's record, the dashboard's list of who registered this morning — knows a
 * registration id and nothing else, and this is what it gets back. `printedBy` and `printedAt` are
 * added by whoever is doing the printing; the server has no view on either.
 */
export interface PrintedSlip {
  communityName: string;
  eventName: string;
  startsOn: string;
  endsOn: string;
  venue: string | null;
  participantName: string;
  registrationNumber: string;
  dancerNumber: string | null;
  entries: Array<{ competition: string; category: string | null }>;
  checkInCode: string;
  checkInUrl: string;
  contact: string | null;
}

export interface RosterRow {
  entryId: string;
  /** Who the row is. The payout table resolves a scan to money owed, and money names a person. */
  participantId: string;
  participantName: string;
  registrationNumber: string;
  dancerNumber: string | null;
  competitionId: string;
  categoryId: string | null;
  categoryName: string | null;
  teamName: string | null;
  teamNumber: string | null;
  checkInId: string | null;
  checkedInAt: number | null;
  /**
   * SHA-256 of every live check-in code this person holds, so a scan resolves on the device with no
   * signal (D66). More than one when a slip has been reprinted (D85) — each is a key to the same
   * registration, and the gate accepts any of them.
   */
  checkInCodeHashes: string[];
}

export interface DimensionValue {
  id: string;
  label: string;
  minValue: number | null;
  maxValue: number | null;
  sortOrder: number;
}

export interface Dimension {
  id: string;
  name: string;
  kind: "nominal" | "numeric_range";
  required: boolean;
  sortOrder: number;
  values: DimensionValue[];
}

/** "For Age Group in 0–7, collapse Dance Style" (D18). Carries its id, because the screen that
 *  lists rules puts a delete beside each one. */
export interface CollapseRule {
  id: string;
  collapsedDimensionId: string;
  whenDimensionId: string;
  whenValueId: string;
  labelTemplate: string | null;
}

export interface PrizeScopeEntry {
  competitionId: string | null;
  categoryId: string | null;
  valueId: string | null;
  /** A role rather than a competition — the elders, the MC, the host drum (D63). It stands on its
   *  own, so a scope carrying one carries nothing else. */
  eventRoleId?: string | null;
}

/** A competition paired with its own dimensions, so the prize dialog can offer a band within it. */
export interface CompetitionDimensions {
  competition: Competition;
  dimensions: Dimension[];
  /** Its categories, so a prize rule can name one, several, or all of them (D52). */
  categories: Array<{ id: string; name: string }>;
}

export interface PrizeRuleSummary {
  id: string;
  name: string;
  sentence: string;
  /** The targets this rule applies to (D52); an empty array is the whole event. */
  scopes: PrizeScopeEntry[];
  basis: "participation" | "placement" | "pooled" | "agreed";
  eligibility: string;
  eligibilityRankCutoff: number | null;
  amountCents: number | null;
  poolTotalCents: number | null;
  poolSplit: "equal" | "by_placement" | null;
  placements: Array<{ place: number; amountCents: number }>;
  cadence: string;
  requiresCheckin: boolean;
  active: boolean;
}

/* -------------------------------------------------- community and members */

export interface SponsorLogo {
  name: string;
  url?: string;
}

/** `brandingSchema` in @powwow/validation. Every field optional — the tokens are the fallback. */
export interface Branding {
  primary?: string;
  primaryContrast?: string;
  secondary?: string;
  secondaryContrast?: string;
  eventLogo?: string;
  /** Superseded by `heroImages`. Still read, so a banner set before the list existed still shows. */
  heroImage?: string;
  /** The banner strip across the top of the public page, in order. */
  heroImages?: string[];
  favicon?: string;
  welcomeMessage?: string;
  footerText?: string;
  sponsorLogos?: SponsorLogo[];
  contactInfo?: string;
}

export interface TermOverride {
  one?: string;
  other?: string;
}

export interface CommunitySettings {
  id: string;
  slug: string;
  name: string;
  legalName: string | null;
  publicName: string | null;
  shortName: string | null;
  description: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  mailingAddress: string | null;
  website: string | null;
  /** Province or territory code. What the community directory is browsed by. */
  region: string | null;
  timezone: string;
  defaultLocale: string;
  supportedLocalesJson: string[] | null;
  currency: string;
  brandingJson: Branding | null;
  terminologyJson: Record<string, TermOverride | string> | null;
  status: "active" | "suspended" | "archived";
}

export interface MemberGrant {
  grantId: string;
  roleId: string;
  roleKey: string;
  roleName: string;
  scopeType: string;
  scopeId: string | null;
}

export interface Member {
  userId: string;
  name: string;
  preferredName: string | null;
  email: string | null;
  phone: string | null;
  status: "active" | "suspended" | "left";
  joinedAt: number;
  grants: MemberGrant[];
  lastSeenAt: number | null;
}

/**
 * A role, with the levels it may be granted at — and the levels *this caller* may grant it at.
 *
 * Two lists rather than one, because they answer different questions and the screen needs both.
 * `scopes` is `ROLE_SCOPES` in @powwow/validation: where the role makes any sense at all, which is
 * a property of the role. `assignableScopes` is a property of whoever is asking — an event manager
 * holds `roles.assign` on one event, so they may appoint a registrar there and nowhere else, and
 * the difference between the two lists is what the screen must not offer.
 *
 * The narrowing is a courtesy, never the control: `grantRole` re-derives both guards server-side
 * and refuses in words, which is what the dialog shows when this list turns out to be optimistic.
 */
export interface AssignableRole {
  id: string;
  key: string;
  name: string;
  description: string;
  /** What the role lets somebody do, in the vocabulary's own words — `PERMISSIONS`. */
  permissions: Permission[];
  scopes: GrantScope[];
  assignableScopes: GrantScope[];
}

/** `grantRoleSchema` in @powwow/validation — exactly one of `roleId` and `roleKey`. */
export interface GrantRequest {
  roleId?: string;
  roleKey?: string;
  scopeType: GrantScope;
  scopeId: string | null;
  /** A grant that lapses on its own — the weekend judge who should not still be one in October. */
  expiresAt?: number | null;
}

export interface Invitation {
  id: string;
  email: string;
  roleId: string;
  roleKey: string | null;
  expiresAt: number;
  lastSentAt: number | null;
  expired: boolean;
}

export interface EmailJob {
  id: string;
  to: string;
  template: string;
  status: "queued" | "running" | "complete" | "failed";
  attempts: number;
  lastError: string | null;
  sentAt: number | null;
  createdAt: number;
}

/* -------------------------------------------------------------------- forms */

export interface FormSummary {
  id: string;
  eventId: string | null;
  name: string;
  purpose: "registration" | "survey" | "consent" | "generic";
  isTemplate: boolean;
  liveVersion: number | null;
  hasDraft: boolean;
}

export interface FormVersion {
  id: string;
  formId: string;
  version: number;
  publishedAt: number | null;
  schemaJson: unknown;
}

export interface FormSubmissionRow {
  id: string;
  formVersionId: string;
  version: number;
  dataJson: unknown;
  submittedAt: number;
  submittedBy: string | null;
}

/* ------------------------------------------------------------- participants */

export interface ParticipantDetail {
  id: string;
  firstName: string;
  lastName: string;
  preferredName: string | null;
  publicDisplayName: string | null;
  communityAffiliation: string | null;
  phone: string | null;
  email: string | null;
  gender: string | null;
  address: string | null;
  city: string | null;
  region: string | null;
  postalCode: string | null;
  country: string | null;
  guardianName: string | null;
  guardianPhone: string | null;
  emergencyContactName: string | null;
  emergencyContactPhone: string | null;
  notes: string | null;
  status: "active" | "inactive" | "merged";
  mergedIntoId: string | null;
}

export interface ParticipantProfile {
  participant: ParticipantDetail;
  registrations: Array<{
    id: string;
    eventId: string;
    eventName: string | null;
    eventSlug: string | null;
    registrationNumber: string;
    dancerNumber: string | null;
    age: number | null;
    registeredAt: number;
    entries: Array<{
      id: string;
      competitionId: string;
      competitionName: string | null;
      categoryId: string | null;
      categoryName: string | null;
      status: "entered" | "withdrawn" | "no_show";
    }>;
  }>;
  checkIns: Array<{ entryId: string; sessionId: string; checkedInAt: number }>;
  payouts: Array<{
    id: string;
    eventId: string;
    amountCents: number;
    currency: string;
    status: string;
  }>;
  documents: Array<{ id: string; type: string; verifiedAt: number | null }>;
  consents: Array<{ type: string; granted: boolean; grantedAt: number; textVersion: string }>;
}

/* -------------------------------------------------------------------- teams */

export interface TeamMember {
  id: string;
  participantId: string;
  roleLabel: string | null;
  participantName: string;
}

export interface Team {
  id: string;
  eventId: string;
  competitionId: string;
  categoryId: string | null;
  name: string;
  teamNumber: string | null;
  captainParticipantId: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  communityAffiliation: string | null;
  status: "forming" | "registered" | "withdrawn";
  distributionNote: string | null;
  notes: string | null;
  members: TeamMember[];
}

/* ------------------------------------------------------------ announcements */

export interface Announcement {
  id: string;
  eventId: string;
  competitionId: string | null;
  title: string;
  body: string;
  publishAt: number | null;
  publishedAt: number | null;
  expiresAt: number | null;
  priority: "normal" | "important" | "urgent";
  visibility: "public" | "internal";
  pinned: boolean;
  emailParticipants: boolean;
  channel: "site" | "push" | "both";
}

/* ------------------------------------------------------------------ images */

/**
 * One stored image (D71).
 *
 * `url` arrives from the API as a path rather than an absolute address, because the public site
 * serves the same image from its own hostname. Here it is prefixed with the API base — this app
 * has no community hostname to hang it off, and does not need one.
 */
export interface AttachmentImage {
  id: string;
  url: string;
  altText: string | null;
  width: number | null;
  height: number | null;
  filename: string;
  sizeBytes: number;
}

export interface EventMedia {
  posters: AttachmentImage[];
  announcementImages: Record<string, AttachmentImage[]>;
  limits: { maxBytes: number; formats: readonly string[] };
}

/** Where this app loads an image from. The site uses its own `/media/{id}` proxy instead. */
export function imageSrc(image: AttachmentImage): string {
  return `${BASE_URL}${image.url}`;
}

/** One piece of a community's artwork, and the string that goes in `branding_json` to use it. */
export interface BrandImage extends AttachmentImage {
  src: string;
}

/**
 * A branding image as this app has to request it.
 *
 * The stored value is one of two things — a `/media/{id}` path for an upload, or a link to
 * somewhere else — and only the first needs help. That path is what the *public site* serves,
 * through its own proxy; this app is on another origin entirely, so it has to ask the API directly.
 * Resolving here rather than storing an absolute URL is what lets one row render correctly on the
 * site, in this app, and on a venue hub with no route to any of them (see `isMediaPath`).
 */
export function brandImageSrc(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const id = value.trim().match(/^\/media\/([0-9A-HJKMNP-TV-Z]{26})$/i)?.[1];
  return id ? `${BASE_URL}/v1/media/${id}` : (safeUrl(value) ?? undefined);
}

/* ----------------------------------------------------------- notifications */

/** `NotificationRow` in @powwow/domain. The readiness rules, addressed to a person (D35). */
export interface NotificationRow {
  id: string;
  userId: string;
  eventId: string | null;
  type: string;
  title: string;
  body: string | null;
  priority: "normal" | "important" | "urgent";
  entityType: string | null;
  entityId: string | null;
  actionPath: string | null;
  readAt: number | null;
  createdAt: number;
}

/* ---------------------------------------------------------------- analytics */

/**
 * One labelled bar. The label travels with the number on purpose: the same payload renders the
 * chart *and* the accessible table beneath it, and a value with its label living in the client
 * would mean the table and the picture could disagree (ux.md §2).
 */
export interface AnalyticsRow {
  key: string;
  label: string;
  value: number;
  /** The denominator, where the number is a completion rather than a count. */
  of?: number;
}

export interface AnalyticsSeries {
  key: string;
  label: string;
  unit: "count" | "cents" | "points";
  rows: AnalyticsRow[];
  total: number;
}

export interface EventAnalytics {
  eventId: string;
  eventName: string;
  totals: {
    registrations: number;
    participants: number;
    entries: number;
    checkedInEntries: number;
    checkInRatePercent: number;
    payoutCents: number;
  };
  series: AnalyticsSeries[];
}

export interface EventComparison {
  events: Array<{
    eventId: string;
    name: string;
    startsOn: string;
    registrations: number;
    participants: number;
    payoutCents: number;
  }>;
}

/* ------------------------------------------------------------------ exports */

/**
 * The report catalogue, re-exported from `@powwow/validation` rather than restated.
 *
 * It was a copy, and the copy went stale the first time a report was added: the server accepted
 * `category_roster` while this list did not have it, so the screen could not offer a report the API
 * would happily generate. Unlike the *column* lists in `exports.tsx` — which have no route to read
 * them from and degrade safely when they drift — this is a plain constant already shared with the
 * server, so there was never a reason to hold a second one.
 */
export { EXPORT_TYPES } from "@powwow/validation";
export type { ExportType } from "@powwow/validation";

export interface ExportJobRow {
  id: string;
  type: string;
  paramsJson: {
    eventId?: string;
    fields?: string[];
    filters?: Record<string, string>;
    includeSensitive?: boolean;
    /** Absent on rows written before the format was a choice — see D69, D97. */
    format?: "csv" | "xlsx" | "pdf" | "print";
    rowCount?: number;
  } | null;
  status: "queued" | "running" | "complete" | "failed";
  requestedBy: string;
  completedAt: number | null;
  createdAt: number;
}

/** One report, as the rows and headings behind it — what the browser prints instead of a CSV. */
export interface ExportReport {
  jobId: string;
  filename: string;
  rowCount: number;
  title: string;
  /**
   * Whose sheet this is and what is on it — generated with the rows, never assembled here.
   *
   * A payout sheet is printed, signed, carried across a table and found in a drawer a year later,
   * and the question asked of it each time is *whose is this*. Building the answer on this side
   * would mean the workbook, the printed page and the CSV could each say something slightly
   * different; taking it from the server means they cannot.
   */
  heading: {
    title: string;
    community: string;
    event: string;
    generatedAt: string;
    sensitiveNotice: string | null;
    /** What the report was narrowed to, resolved to names by the server — never to ids, which mean
     *  nothing to the person holding the sheet. */
    filters: Array<{ label: string; value: string }>;
  };
  columns: Array<{ key: string; label: string; sensitive?: boolean }>;
  rows: Array<Record<string, string | number | boolean | null | undefined>>;
}

export interface ExportRequest {
  type: ExportType;
  /**
   * `csv` and `pdf` download a file straight from the server — the PDF is rendered there now, with
   * the fonts a name in this system needs embedded in it (D97). `xlsx` and `print` return the
   * report as rows, for the browser to build a workbook from (D81) or lay out under `@media print`
   * (D96).
   */
  format?: "csv" | "xlsx" | "pdf" | "print";
  fields?: string[];
  filters?: {
    competitionId?: string;
    categoryId?: string;
    sessionId?: string;
    surveyId?: string;
    status?: string;
    from?: string;
    to?: string;
  };
  includeSensitive?: boolean;
  includeHeader?: boolean;
}

/* ------------------------------------------------------------------ surveys */

export type SurveyAudience =
  "participants" | "judges" | "organizers" | "volunteers" | "vendors" | "public";

export interface SurveyRow {
  id: string;
  eventId: string | null;
  name: string;
  description: string | null;
  audience: SurveyAudience;
  anonymous: boolean;
  formVersionId: string;
  opensAt: number | null;
  closesAt: number | null;
}

export interface QuestionAggregate {
  key: string;
  label: string;
  type: string;
  answered: number;
  average?: number;
  distribution?: Array<{ key: string; label: string; value: number }>;
  /** Free text, in submission order. Never summarised — the words are the point. */
  answers?: string[];
}

export interface SurveyResults {
  survey: SurveyRow;
  invited: number;
  responded: number;
  responseRatePercent: number;
  questions: QuestionAggregate[];
}

/* -------------------------------------------------------------------- audit */

export interface AuditEntryRow {
  id: string;
  actorUserId: string | null;
  actorName: string;
  actorType: string;
  action: string;
  entityType: string;
  entityId: string;
  reason: string | null;
  createdAt: number;
  before: unknown;
  after: unknown;
}

export interface AuditQuery {
  entityType?: string;
  entityId?: string;
  actorUserId?: string;
  action?: string;
  eventId?: string;
  from?: number;
  to?: number;
  limit?: number;
  cursor?: string;
}

/* ----------------------------------------------------------------- QR codes */

/** The server returns URLs only; encoding one is a rendering concern (`@powwow/ui`'s `qrSvg`). */
export interface QrTarget {
  key: string;
  label: string;
  url: string;
  /** The DNS-less alternate on the apex, present for public-site codes only. */
  fallbackUrl?: string;
}

/* ------------------------------------------------------------------- phases */

export type AdvancementRule =
  | { kind: "all" }
  | { kind: "manual" }
  | { kind: "top_n"; count: number }
  | { kind: "top_percent"; percent: number }
  | { kind: "min_score"; points: number }
  | { kind: "pass_fail" }
  | { kind: "organizer_approval" }
  | { kind: "head_judge_approval" };

export interface Phase {
  id: string;
  competitionId: string;
  name: string;
  /** The session this round runs in (D53), or null to follow the category or competition. */
  sessionId: string | null;
  sortOrder: number;
  advancementRule: AdvancementRule | null;
}

export interface AdvancementOutcome {
  advancing: string[];
  requiresDecision: boolean;
  explanation: string;
}

/* -------------------------------------------------- tabulation adjustments */

export type AdjustmentKind = "penalty" | "deduction" | "bonus" | "correction";

export interface Adjustment {
  id: string;
  registrationEntryId: string;
  categoryId: string;
  phaseId: string | null;
  kind: AdjustmentKind;
  /** Signed, in display points — a penalty of five is stored as -5 (D34). */
  points: number;
  label: string;
  reason: string;
  appliedBy: string | null;
  createdAt: number;
}

/** The stored standing, row by row. `overrideRank` wins over `rank` wherever both are shown. */
export interface ResultRow {
  id: string;
  registrationEntryId: string;
  rank: number;
  total: number;
  tieBreakApplied: string | null;
  overrideRank: number | null;
  overrideReason: string | null;
}

/** `CalculationTrace` from @powwow/scoring-engine, as the results route returns it. */
export interface CalculationTrace {
  engineVersion: string;
  method: "sum" | "average" | "drop_high_low" | "rank" | "pass_fail";
  scoringConfigVersion: number;
  entries: Array<{
    entryId: string;
    judges: Array<{
      judgeId: string;
      criteria: Array<{
        criterionId: string;
        name: string;
        raw: number;
        weightBp: number;
        weighted: number;
      }>;
      total: number;
      dropped?: boolean;
      abstained?: boolean;
      abstentionReason?: string;
      decision?: string;
    }>;
    countedJudges: number;
    total: number;
    baseTotal?: number;
    adjustments?: Array<{
      kind: AdjustmentKind;
      label: string;
      points: number;
      delta: number;
      reason: string;
    }>;
    lowerIsBetter: boolean;
    passed?: boolean;
  }>;
  tieBreaks: Array<{
    rule: string;
    entryIds: string[];
    values: Array<{ entryId: string; value: number | null }>;
    resolved: boolean;
  }>;
  warnings: Warning[];
}

/* ------------------------------------------------------------------- api */

export const api = {
  events: (communityId: string) =>
    call<{ events: EventSummary[] }>(communityId, "/v1/manage/events"),

  event: (communityId: string, eventId: string) =>
    call<{ event: EventSummary; sessions: SessionSummary[]; windows: WindowSummary[] }>(
      communityId,
      `/v1/manage/events/${eventId}`,
    ),

  readiness: (communityId: string, eventId: string) =>
    call<Readiness>(communityId, `/v1/manage/events/${eventId}/readiness`),

  publish: (communityId: string, eventId: string) =>
    call<{ event: EventSummary }>(communityId, `/v1/manage/events/${eventId}/publish`, {
      method: "POST",
    }),

  competitions: (communityId: string, eventId: string) =>
    call<{ competitions: Competition[] }>(communityId, `/v1/manage/events/${eventId}/competitions`),

  /** The whole programme in one call, for reading it by session — see `sessionProgramme`. */
  eventSchedule: (communityId: string, eventId: string) =>
    call<EventSchedule>(communityId, `/v1/manage/events/${eventId}/schedule`),

  categories: (communityId: string, competitionId: string) =>
    call<{ categories: Category[] }>(
      communityId,
      `/v1/manage/competitions/${competitionId}/categories`,
    ),

  categoryPreview: (communityId: string, competitionId: string) =>
    call<CategoryPreview>(
      communityId,
      `/v1/manage/competitions/${competitionId}/categories/preview`,
    ),

  commitCategories: (
    communityId: string,
    competitionId: string,
    body: { categories: ProposedCategory[]; archiveMissing: boolean },
  ) =>
    call<{ categories: Category[] }>(
      communityId,
      `/v1/manage/competitions/${competitionId}/categories/commit`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  /** A category the organiser typed. Generation never archives one of these (D43). */
  createCategory: (communityId: string, competitionId: string, body: unknown) =>
    call<{ category: Category }>(
      communityId,
      `/v1/manage/competitions/${competitionId}/categories`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  updateCategory: (communityId: string, categoryId: string, body: unknown) =>
    call<{ category: Category }>(communityId, `/v1/manage/categories/${categoryId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  deleteCategory: (communityId: string, categoryId: string) =>
    call<{ ok: true }>(communityId, `/v1/manage/categories/${categoryId}`, { method: "DELETE" }),

  /** The order the registration form and the desk list them in (D68). Sent whole, every id once. */
  reorderCategories: (communityId: string, competitionId: string, categoryIds: string[]) =>
    call<{ categories: Category[] }>(
      communityId,
      `/v1/manage/competitions/${competitionId}/categories/reorder`,
      { method: "POST", body: JSON.stringify({ categoryIds }) },
    ),

  createEvent: (communityId: string, body: unknown) =>
    call<{ event: EventSummary }>(communityId, "/v1/manage/events", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updateEvent: (communityId: string, eventId: string, body: unknown) =>
    call<{ event: EventSummary }>(communityId, `/v1/manage/events/${eventId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  deleteEvent: (communityId: string, eventId: string) =>
    call<{ ok: true }>(communityId, `/v1/manage/events/${eventId}`, { method: "DELETE" }),

  createSession: (communityId: string, eventId: string, body: unknown) =>
    call<{ session: SessionSummary }>(communityId, `/v1/manage/events/${eventId}/sessions`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updateSession: (communityId: string, sessionId: string, body: unknown) =>
    call<{ session: SessionSummary }>(communityId, `/v1/manage/sessions/${sessionId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  deleteSession: (communityId: string, sessionId: string) =>
    call<{ ok: true }>(communityId, `/v1/manage/sessions/${sessionId}`, { method: "DELETE" }),

  createWindow: (communityId: string, eventId: string, body: unknown) =>
    call<{ ok: true }>(communityId, `/v1/manage/events/${eventId}/windows`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updateWindow: (communityId: string, windowId: string, body: unknown) =>
    call<{ ok: true }>(communityId, `/v1/manage/windows/${windowId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  deleteWindow: (communityId: string, windowId: string) =>
    call<{ ok: true }>(communityId, `/v1/manage/windows/${windowId}`, { method: "DELETE" }),

  createCompetition: (communityId: string, eventId: string, body: unknown) =>
    call<{ competition: Competition }>(communityId, `/v1/manage/events/${eventId}/competitions`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updateCompetition: (communityId: string, competitionId: string, body: unknown) =>
    call<{ competition: Competition }>(communityId, `/v1/manage/competitions/${competitionId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  deleteCompetition: (communityId: string, competitionId: string) =>
    call<{ ok: true }>(communityId, `/v1/manage/competitions/${competitionId}`, {
      method: "DELETE",
    }),

  createDimension: (communityId: string, competitionId: string, body: unknown) =>
    call<{ dimension: Dimension }>(
      communityId,
      `/v1/manage/competitions/${competitionId}/dimensions`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  dimensions: (communityId: string, competitionId: string) =>
    call<{ dimensions: Dimension[]; collapseRules: CollapseRule[] }>(
      communityId,
      `/v1/manage/competitions/${competitionId}/dimensions`,
    ),

  updateDimension: (communityId: string, dimensionId: string, body: unknown) =>
    call<{ dimension: Dimension }>(communityId, `/v1/manage/dimensions/${dimensionId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  deleteDimension: (communityId: string, dimensionId: string) =>
    call<{ ok: true }>(communityId, `/v1/manage/dimensions/${dimensionId}`, { method: "DELETE" }),

  /* ------------------------------------------------------ dimension templates */

  /** The community's usual bands, kept once and copied into a competition (D43). */
  dimensionTemplates: (communityId: string) =>
    call<{ templates: Dimension[] }>(communityId, "/v1/manage/dimension-templates"),

  createDimensionTemplate: (communityId: string, body: unknown) =>
    call<{ template: Dimension }>(communityId, "/v1/manage/dimension-templates", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  applyDimensionTemplate: (communityId: string, competitionId: string, templateId: string) =>
    call<{ dimension: Dimension }>(
      communityId,
      `/v1/manage/competitions/${competitionId}/dimensions/apply-template`,
      { method: "POST", body: JSON.stringify({ templateId }) },
    ),

  saveDimensionAsTemplate: (communityId: string, dimensionId: string, name?: string) =>
    call<{ template: Dimension }>(
      communityId,
      `/v1/manage/dimensions/${dimensionId}/save-as-template`,
      { method: "POST", body: JSON.stringify(name ? { name } : {}) },
    ),

  createCollapseRule: (communityId: string, competitionId: string, body: unknown) =>
    call<{ ok: true }>(communityId, `/v1/manage/competitions/${competitionId}/collapse-rules`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  deleteCollapseRule: (communityId: string, ruleId: string) =>
    call<{ ok: true }>(communityId, `/v1/manage/collapse-rules/${ruleId}`, { method: "DELETE" }),

  scoringConfig: (communityId: string, competitionId: string) =>
    call<{ config: ScoringConfigDetail | null }>(
      communityId,
      `/v1/manage/competitions/${competitionId}/scoring`,
    ),

  createScoringConfig: (communityId: string, competitionId: string, body: unknown) =>
    call<{ id: string }>(communityId, `/v1/manage/competitions/${competitionId}/scoring`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  prizeRules: (communityId: string, eventId: string) =>
    call<{ rules: PrizeRuleSummary[] }>(communityId, `/v1/manage/events/${eventId}/prize-rules`),

  /* ------------------------------------------------------------ event roles */

  /**
   * Named `eventRoles`, not `roles` — `api.roles` already means the *permission* roles a member can
   * be granted, and the paths are distinct for the same reason. Two unrelated concepts sharing a
   * word is how somebody eventually grants the MC a login.
   */
  eventRoles: (communityId: string, eventId: string) =>
    call<{ roles: EventRoleSummary[]; assignments: RoleAssignmentSummary[] }>(
      communityId,
      `/v1/manage/events/${eventId}/event-roles`,
    ),

  eventRoleTemplates: (communityId: string) =>
    call<{ roles: EventRoleSummary[] }>(communityId, `/v1/manage/event-role-templates`),

  createEventRole: (communityId: string, eventId: string, body: unknown) =>
    call<{ id: string }>(communityId, `/v1/manage/events/${eventId}/event-roles`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  createEventRoleTemplate: (communityId: string, body: unknown) =>
    call<{ id: string }>(communityId, `/v1/manage/event-role-templates`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  applyEventRoleTemplates: (communityId: string, eventId: string) =>
    call<{ copied: number }>(
      communityId,
      `/v1/manage/events/${eventId}/event-roles/apply-templates`,
      { method: "POST", body: JSON.stringify({}) },
    ),

  saveEventRolesAsTemplates: (communityId: string, eventId: string) =>
    call<{ saved: number }>(
      communityId,
      `/v1/manage/events/${eventId}/event-roles/save-as-templates`,
      { method: "POST", body: JSON.stringify({}) },
    ),

  updateEventRole: (communityId: string, roleId: string, body: unknown) =>
    call<{ ok: true }>(communityId, `/v1/manage/event-roles/${roleId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  archiveEventRole: (communityId: string, roleId: string) =>
    call<{ ok: true }>(communityId, `/v1/manage/event-roles/${roleId}`, { method: "DELETE" }),

  assignEventRole: (communityId: string, eventId: string, body: unknown) =>
    call<{ id: string }>(communityId, `/v1/manage/events/${eventId}/role-assignments`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updateRoleAssignment: (communityId: string, assignmentId: string, body: unknown) =>
    call<{ ok: true }>(communityId, `/v1/manage/role-assignments/${assignmentId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  removeRoleAssignment: (communityId: string, assignmentId: string) =>
    call<{ ok: true }>(communityId, `/v1/manage/role-assignments/${assignmentId}`, {
      method: "DELETE",
    }),

  createPrizeRule: (communityId: string, eventId: string, body: unknown) =>
    call<{ id: string }>(communityId, `/v1/manage/events/${eventId}/prize-rules`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updatePrizeRule: (communityId: string, ruleId: string, body: unknown) =>
    call<{ ok: true }>(communityId, `/v1/manage/prize-rules/${ruleId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  archivePrizeRule: (communityId: string, ruleId: string) =>
    call<{ ok: true }>(communityId, `/v1/manage/prize-rules/${ruleId}/archive`, { method: "POST" }),

  sessionRoster: (communityId: string, eventId: string, sessionId: string) =>
    call<{ roster: RosterRow[] }>(
      communityId,
      `/v1/ops/events/${eventId}/sessions/${sessionId}/roster`,
    ),

  /**
   * Force a window open or closed during the event, or hand it back to its schedule.
   *
   * `scheduled` is the undo. Works on check-in windows exactly as on registration ones — they are
   * one table resolved by one rule, and this route never asked which kind it was given.
   */
  setWindowState: (
    communityId: string,
    windowId: string,
    status: "scheduled" | "open" | "closed",
  ) =>
    call<{ ok: true }>(communityId, `/v1/manage/windows/${windowId}/state`, {
      method: "POST",
      body: JSON.stringify({ status }),
    }),

  /**
   * The same act said the way the gate says it: *this session, open now*.
   *
   * The check-in screen has a session, not a window, and most events have no check-in window at all
   * — the server finds the one that governs the session or writes the derived bounds down as one.
   */
  setCheckInGate: (
    communityId: string,
    eventId: string,
    sessionId: string,
    status: "scheduled" | "open" | "closed",
  ) =>
    call<{ windowId: string | null }>(communityId, `/v1/manage/events/${eventId}/check-in-gate`, {
      method: "POST",
      body: JSON.stringify({ sessionId, status }),
    }),

  /**
   * The communities the platform knows, for the home-community picker.
   *
   * Name and province only — the same projection the public form gets. Cross-tenant, because the
   * community a dancer is *from* is usually not the community running the event (D46).
   */
  communityDirectory: (communityId: string) =>
    call<{ communities: Array<{ id: string; name: string; region: string | null }> }>(
      communityId,
      `/v1/ops/community-directory`,
    ),

  /**
   * Put somebody on file who is not registering for anything.
   *
   * The desk creates a participant on the way to a registration; a roster holder — an MC, an elder,
   * a drum's lead — never registers at all, and still has to be a person before they can be paid.
   */
  createParticipant: (communityId: string, input: { firstName: string; lastName: string }) =>
    call<{ participant: Participant }>(communityId, `/v1/ops/participants`, {
      method: "POST",
      body: JSON.stringify(input),
    }),

  searchParticipants: (communityId: string, query: string, eventId?: string) =>
    call<{ participants: Participant[] }>(
      communityId,
      // The event lets a registration number (`REG-0042`) match the person who holds it, not only
      // a name; omitted, the search is name-only across the community.
      `/v1/ops/participants?q=${encodeURIComponent(query)}${
        eventId ? `&event=${encodeURIComponent(eventId)}` : ""
      }`,
    ),

  /**
   * Everyone registered for one event — the participants screen's opening list, before anybody
   * has typed. The same route with no `q`, because it is the same question with no name on it.
   */
  eventParticipants: (communityId: string, eventId: string) =>
    call<{ participants: Participant[] }>(
      communityId,
      `/v1/ops/participants?event=${encodeURIComponent(eventId)}`,
    ),

  registrations: (communityId: string, eventId: string) =>
    call<{ registrations: RegistrationDetail[]; canOverrideWindow: boolean }>(
      communityId,
      `/v1/ops/events/${eventId}/registrations`,
    ),

  register: (communityId: string, eventId: string, body: unknown) =>
    call<{
      registration: {
        id: string;
        participantId: string;
        registrationNumber: string;
        dancerNumber: string | null;
      };
      /**
       * The gate code and the address it stands for, so the desk can print the slip from what it
       * already has rather than asking again (D85). Minted for every registration, with or without
       * an email address to send it to — the walk-up is the case the printed slip exists for.
       */
      checkInCode: string;
      checkInUrl: string;
    }>(communityId, `/v1/ops/events/${eventId}/registrations`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /**
   * Print a slip again — after a jam, a torn roll, or a dancer who put theirs down somewhere (D85).
   *
   * Mints a fresh gate code, because only the hash of the first one was kept. The old code stays
   * live: both open the same registration, and a check-in is unique on entry and session, so a
   * second slip cannot mean a second payout.
   */
  reprintSlip: (communityId: string, registrationId: string) =>
    call<{ slip: PrintedSlip }>(communityId, `/v1/ops/registrations/${registrationId}/slip`, {
      method: "POST",
    }),

  /**
   * Take somebody out of a competition, or put them back.
   *
   * Not `disqualifyEntry`, which writes the same status and means a ruling — this is the registrar's
   * correction, and the two are kept apart all the way down to the audit log.
   */
  setEntryStatus: (
    communityId: string,
    entryId: string,
    status: "entered" | "withdrawn" | "no_show",
    reason?: string,
  ) =>
    call<{ ok: true }>(communityId, `/v1/ops/entries/${entryId}`, {
      method: "PATCH",
      body: JSON.stringify({ status, ...(reason ? { reason } : {}) }),
    }),

  /** The live registration form for the desk, or null when none is published (D3). */
  registrationForm: (communityId: string, eventId: string) =>
    call<{ form: { versionId: string; version: number; definition: FormDefinition } | null }>(
      communityId,
      `/v1/ops/events/${eventId}/registration-form`,
    ),

  /** Which desks have registered at this event, and how many machines each one is (D86). */
  deskStations: (communityId: string, eventId: string) =>
    call<{
      stations: Array<{
        station: number;
        devices: string[];
        registrations: number;
        lastAt: number;
      }>;
    }>(communityId, `/v1/ops/events/${eventId}/desk-stations`),

  sessionCounts: (communityId: string, eventId: string, sessionId: string) =>
    call<{ expected: number; checkedIn: number }>(
      communityId,
      `/v1/ops/events/${eventId}/sessions/${sessionId}/counts`,
    ),

  lookupDancer: (communityId: string, eventId: string, dancerNumber: string) =>
    call<{ entries: LookupEntry[] }>(
      communityId,
      `/v1/ops/events/${eventId}/lookup/${encodeURIComponent(dancerNumber)}`,
    ),

  checkIn: (
    communityId: string,
    eventId: string,
    checkIns: Array<{
      id: string;
      sessionId: string;
      registrationEntryId: string;
      checkedInAt: number;
      method: "qr" | "manual" | "bulk";
      deviceId?: string;
    }>,
  ) =>
    call<{ outcomes: Array<{ id: string; created: boolean; checkedInAt: number }> }>(
      communityId,
      `/v1/ops/events/${eventId}/check-ins`,
      { method: "POST", body: JSON.stringify({ checkIns }) },
    ),

  reverseCheckIn: (communityId: string, checkInId: string, reason: string) =>
    call<{ ok: true }>(communityId, `/v1/ops/check-ins/${checkInId}/reverse`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  roster: (communityId: string) => call<{ roster: RosterEntry[] }>(communityId, "/v1/ops/judges"),

  removeFromRoster: (communityId: string, rosterId: string) =>
    call<{ ok: true }>(communityId, `/v1/ops/judges/${rosterId}`, { method: "DELETE" }),

  assignments: (communityId: string, sessionId: string) =>
    call<{ assignments: Assignment[] }>(communityId, `/v1/ops/sessions/${sessionId}/assignments`),

  assignJudge: (communityId: string, eventId: string, body: unknown) =>
    call<{ assignment: Assignment; accessCode: string | undefined }>(
      communityId,
      `/v1/ops/events/${eventId}/assignments`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  revokeAssignment: (communityId: string, assignmentId: string, reason: string) =>
    call<{ ok: true }>(communityId, `/v1/ops/assignments/${assignmentId}/revoke`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  reissueCode: (communityId: string, assignmentId: string) =>
    call<{ accessCode: string }>(communityId, `/v1/ops/assignments/${assignmentId}/reissue-code`, {
      method: "POST",
    }),

  /** `sessionId` says which occasion, for a category that was entered on more than one. */
  tabulate: (
    communityId: string,
    categoryId: string,
    competitionId: string,
    sessionId?: string | undefined,
  ) =>
    call<{ resultSet: ResultSet; placings: Placing[]; warnings: Warning[] }>(
      communityId,
      `/v1/ops/categories/${categoryId}/tabulate?competitionId=${competitionId}` +
        (sessionId ? `&sessionId=${sessionId}` : ""),
      { method: "POST" },
    ),

  /**
   * The stored standing and the trace that produced it.
   *
   * `results` is the persisted row set — it carries the row id an override needs, and the override
   * itself. `placings` on the tabulate response is the engine's own output and has no row id, which
   * is why the two shapes are different rather than duplicated.
   */
  results: (communityId: string, categoryId: string) =>
    call<{
      resultSet: ResultSet | null;
      placings: ResultRow[];
      trace: CalculationTrace | null;
    }>(communityId, `/v1/ops/categories/${categoryId}/results`),

  approveResults: (communityId: string, resultSetId: string, missingScores: number) =>
    call<{ resultSet: ResultSet }>(communityId, `/v1/ops/result-sets/${resultSetId}/approve`, {
      method: "POST",
      body: JSON.stringify({ missingScores }),
    }),

  publishResults: (communityId: string, resultSetId: string) =>
    call<{ resultSet: ResultSet }>(communityId, `/v1/ops/result-sets/${resultSetId}/publish`, {
      method: "POST",
    }),

  calculatePayouts: (communityId: string, eventId: string) =>
    call<{
      written: number;
      voided: number;
      skippedPaid: number;
      totalCents: number;
      pending: Array<{ ruleId: string; ruleName: string; reason: string }>;
      /** In scope and qualifying, but with nobody to hand the money to — a poster credit with no
       *  person and no group behind the name (D62). The API has always said so; the sheet did not,
       *  and a total quietly short by an honorarium is the one gap the sheet cannot help notice. */
      unpayable: Array<{ ruleId: string; ruleName: string; name: string; reason: string }>;
    }>(communityId, `/v1/ops/events/${eventId}/payouts/calculate`, { method: "POST" }),

  payouts: (communityId: string, eventId: string, sessionId?: string) =>
    call<{ payouts: Payout[] }>(
      communityId,
      `/v1/ops/events/${eventId}/payouts${sessionId ? `?sessionId=${sessionId}` : ""}`,
    ),

  approvePayouts: (communityId: string, eventId: string, payoutIds: string[]) =>
    call<{ approved: number }>(communityId, `/v1/ops/events/${eventId}/payouts/approve`, {
      method: "POST",
      body: JSON.stringify({ payoutIds }),
    }),

  payPayouts: (communityId: string, eventId: string, payoutIds: string[], method: string) =>
    call<{ paid: number }>(communityId, `/v1/ops/events/${eventId}/payouts/pay`, {
      method: "POST",
      body: JSON.stringify({ payoutIds, paidMethod: method }),
    }),

  voidPayout: (communityId: string, payoutId: string, reason: string) =>
    call<{ ok: true }>(communityId, `/v1/ops/payouts/${payoutId}/void`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  /** Take a payment back. The line returns to *ready to pay*; the reason stays on the record. */
  unpayPayout: (communityId: string, payoutId: string, reason: string) =>
    call<{ ok: true }>(communityId, `/v1/ops/payouts/${payoutId}/unpay`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  /**
   * Set an amount by hand. Refused once paid.
   *
   * Returns the id of the replacement line, because this voids the one you named — a caller still
   * holding the old id is pointing at a dead row.
   */
  overridePayout: (communityId: string, payoutId: string, amountCents: number, reason: string) =>
    call<{ payoutId: string }>(communityId, `/v1/ops/payouts/${payoutId}/override`, {
      method: "POST",
      body: JSON.stringify({ amountCents, reason }),
    }),

  /* --------------------------------------------------------- event lifecycle */

  duplicateEvent: (communityId: string, eventId: string, options: Partial<DuplicateOptions>) =>
    call<{ event: EventSummary }>(communityId, `/v1/manage/events/${eventId}/duplicate`, {
      method: "POST",
      body: JSON.stringify({ options }),
    }),

  cancelEvent: (communityId: string, eventId: string, reason: string) =>
    call<{ event: EventSummary }>(communityId, `/v1/manage/events/${eventId}/cancel`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  /* ------------------------------------------------------ community settings */

  community: (communityId: string) =>
    call<{ community: CommunitySettings }>(communityId, "/v1/manage/community"),

  updateCommunityProfile: (communityId: string, body: unknown) =>
    call<{ community: CommunitySettings }>(communityId, "/v1/manage/community/profile", {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  updateBranding: (communityId: string, branding: Branding) =>
    call<{ community: CommunitySettings }>(communityId, "/v1/manage/community/branding", {
      method: "PATCH",
      body: JSON.stringify({ branding }),
    }),

  updateTerminology: (communityId: string, terminology: Record<string, TermOverride>) =>
    call<{ community: CommunitySettings }>(communityId, "/v1/manage/community/terminology", {
      method: "PATCH",
      body: JSON.stringify({ terminology }),
    }),

  emails: (communityId: string, status?: EmailJob["status"]) =>
    call<{ emails: EmailJob[] }>(
      communityId,
      `/v1/manage/emails${status ? `?status=${status}` : ""}`,
    ),

  /* ----------------------------------------------------------------- members */

  members: (communityId: string) =>
    call<{ members: Member[]; invitations: Invitation[] }>(communityId, "/v1/manage/members"),

  /** The raw token comes back once, for the person who cannot wait for the email. */
  invite: (communityId: string, body: { email: string; roleId?: string; roleKey?: string }) =>
    call<{ invitationId: string; token: string }>(communityId, "/v1/manage/members/invitations", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  resendInvitation: (communityId: string, invitationId: string) =>
    call<{ token: string }>(communityId, `/v1/manage/members/invitations/${invitationId}/resend`, {
      method: "POST",
    }),

  revokeInvitation: (communityId: string, invitationId: string) =>
    call<{ ok: true }>(communityId, `/v1/manage/members/invitations/${invitationId}/revoke`, {
      method: "POST",
    }),

  changeMemberRole: (communityId: string, userId: string, roleId: string) =>
    call<{ ok: true }>(communityId, `/v1/manage/members/${userId}/role`, {
      method: "POST",
      body: JSON.stringify({ roleId }),
    }),

  limitMemberAccess: (communityId: string, userId: string, eventId: string | null) =>
    call<{ ok: true }>(communityId, `/v1/manage/members/${userId}/access`, {
      method: "POST",
      body: JSON.stringify({ eventId }),
    }),

  deactivateMember: (communityId: string, userId: string) =>
    call<{ ok: true }>(communityId, `/v1/manage/members/${userId}/deactivate`, { method: "POST" }),

  reactivateMember: (communityId: string, userId: string) =>
    call<{ ok: true }>(communityId, `/v1/manage/members/${userId}/reactivate`, { method: "POST" }),

  /* ------------------------------------------------------------ responsibilities */

  /**
   * Every role, with where it fits and where the caller may hand it out.
   *
   * The second half is the one that cannot be computed here: the session carries a permission list
   * flattened across every grant, with the scopes discarded, so a client cannot tell "organiser
   * everywhere" from "event manager on one event". The server can, and does.
   */
  roles: (communityId: string) =>
    call<{ roles: AssignableRole[] }>(communityId, "/v1/manage/roles"),

  /**
   * What the scope ids on a grant are called — "Dance contest, Annual Pow Wow".
   *
   * `kinds` is the levels the screen actually holds grants at, so a community whose grants are all
   * event-scoped never makes the server read a category. One request for the lot; this used to be a
   * walk down the tree, a request per event and then per competition.
   */
  scopeNames: (communityId: string, kinds: readonly string[]) =>
    call<{ names: Array<{ id: string; label: string }> }>(
      communityId,
      `/v1/manage/scope-names?kinds=${encodeURIComponent(kinds.join(","))}`,
    ),

  /**
   * Add one responsibility. Additive — this never disturbs the grants a person already holds.
   *
   * Refused when the role carries a permission the caller does not hold at that scope, or when the
   * scope is outside what they are responsible for. Both refusals come back as sentences, and the
   * dialog shows them beside the attempt rather than turning them into "something went wrong".
   */
  grantResponsibility: (communityId: string, userId: string, body: GrantRequest) =>
    call<{ grantId: string }>(communityId, `/v1/manage/members/${userId}/grants`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /**
   * Take one responsibility back, leaving the rest alone.
   *
   * The path names the grant, not the person, because that is what is being withdrawn — a registrar
   * for Dance who is also a check-in worker for Saturday keeps the second when the first goes.
   *
   * The reason travels in the body the way `removeAdjustment` does. It is what the ConfirmDialog
   * collected and what the audit entry should read back; a server that does not yet look for it
   * simply ignores it, and nothing here depends on it coming back.
   */
  revokeResponsibility: (communityId: string, grantId: string, reason: string) =>
    call<{ ok: true }>(communityId, `/v1/manage/members/grants/${grantId}`, {
      method: "DELETE",
      body: JSON.stringify({ reason }),
    }),

  /* ------------------------------------------------------------------- forms */

  forms: (communityId: string, eventId?: string) =>
    call<{ forms: FormSummary[] }>(
      communityId,
      `/v1/manage/forms${eventId ? `?eventId=${eventId}` : ""}`,
    ),

  createForm: (communityId: string, body: unknown) =>
    call<{ form: FormSummary }>(communityId, "/v1/manage/forms", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  form: (communityId: string, formId: string) =>
    call<{ form: FormSummary; versions: FormVersion[] }>(communityId, `/v1/manage/forms/${formId}`),

  updateForm: (communityId: string, formId: string, body: unknown) =>
    call<{ form: FormSummary }>(communityId, `/v1/manage/forms/${formId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  deleteForm: (communityId: string, formId: string) =>
    call<{ ok: true }>(communityId, `/v1/manage/forms/${formId}`, { method: "DELETE" }),

  /** A PUT, because it replaces the working draft rather than appending a version to it. */
  saveFormDraft: (communityId: string, formId: string, definition: unknown) =>
    call<{ version: FormVersion }>(communityId, `/v1/manage/forms/${formId}/draft`, {
      method: "PUT",
      body: JSON.stringify(definition),
    }),

  publishForm: (communityId: string, formId: string) =>
    call<{ version: FormVersion }>(communityId, `/v1/manage/forms/${formId}/publish`, {
      method: "POST",
    }),

  duplicateForm: (communityId: string, formId: string) =>
    call<{ form: FormSummary }>(communityId, `/v1/manage/forms/${formId}/duplicate`, {
      method: "POST",
    }),

  saveFormAsTemplate: (communityId: string, formId: string) =>
    call<{ form: FormSummary }>(communityId, `/v1/manage/forms/${formId}/template`, {
      method: "POST",
    }),

  formSubmissions: (communityId: string, formId: string) =>
    call<{ submissions: FormSubmissionRow[] }>(
      communityId,
      `/v1/manage/forms/${formId}/submissions`,
    ),

  /* ------------------------------------------------------------ participants */

  participant: (communityId: string, participantId: string) =>
    call<ParticipantProfile>(communityId, `/v1/ops/participants/${participantId}`),

  updateParticipant: (communityId: string, participantId: string, body: unknown) =>
    call<{ participant: ParticipantDetail }>(communityId, `/v1/ops/participants/${participantId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  /** A team member's registration for one event — identity and form answers — to edit (D57). */
  memberRegistration: (communityId: string, eventId: string, participantId: string) =>
    call<{
      participant: ParticipantDetail;
      form: { version: number; definition: FormDefinition } | null;
      values: Record<string, unknown>;
      registrationId: string | null;
      age: number | null;
    }>(communityId, `/v1/ops/events/${eventId}/participants/${participantId}/registration`),

  updateMemberRegistration: (
    communityId: string,
    eventId: string,
    participantId: string,
    body: unknown,
  ) =>
    call<{ ok: boolean }>(
      communityId,
      `/v1/ops/events/${eventId}/participants/${participantId}/registration`,
      { method: "PATCH", body: JSON.stringify(body) },
    ),

  /** The path names the survivor; the body names the duplicate. */
  mergeParticipants: (
    communityId: string,
    survivorId: string,
    duplicateId: string,
    reason: string,
  ) =>
    call<{ participant: ParticipantDetail }>(
      communityId,
      `/v1/ops/participants/${survivorId}/merge`,
      { method: "POST", body: JSON.stringify({ duplicateId, reason }) },
    ),

  /* ------------------------------------------------------------------- teams */

  teams: (communityId: string, eventId: string) =>
    call<{ teams: Team[] }>(communityId, `/v1/ops/events/${eventId}/teams`),

  createTeam: (communityId: string, eventId: string, body: unknown) =>
    call<{ team: Team }>(communityId, `/v1/ops/events/${eventId}/teams`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updateTeam: (communityId: string, teamId: string, body: unknown) =>
    call<{ team: Team }>(communityId, `/v1/ops/teams/${teamId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  archiveTeam: (communityId: string, teamId: string) =>
    call<{ ok: true }>(communityId, `/v1/ops/teams/${teamId}`, { method: "DELETE" }),

  addTeamMember: (
    communityId: string,
    teamId: string,
    body: { participantId: string; roleLabel?: string },
  ) =>
    call<{ team: Team }>(communityId, `/v1/ops/teams/${teamId}/members`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  removeTeamMember: (communityId: string, teamId: string, participantId: string) =>
    call<{ ok: true }>(communityId, `/v1/ops/teams/${teamId}/members/${participantId}`, {
      method: "DELETE",
    }),

  setTeamCaptain: (communityId: string, teamId: string, participantId: string) =>
    call<{ team: Team }>(communityId, `/v1/ops/teams/${teamId}/captain`, {
      method: "POST",
      body: JSON.stringify({ participantId }),
    }),

  /** Registering a team mints registrations for its members, so it carries this desk's station in
   *  the body exactly as an individual registration does (D86). */
  registerTeam: (communityId: string, teamId: string) =>
    call<{ team: Team; entryId: string; teamNumber: string }>(
      communityId,
      `/v1/ops/teams/${teamId}/register`,
      { method: "POST", body: JSON.stringify(stationBody()) },
    ),

  /** Create a team and all its members in one submission, then register it (D57). Each member is
   *  either an existing participant (`participantId`) or a new person named inline. */
  registerTeamTogether: (communityId: string, eventId: string, body: unknown) =>
    call<{ team: Team; entryId: string; teamNumber: string }>(
      communityId,
      `/v1/ops/events/${eventId}/teams/register-together`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  /* ----------------------------------------------------------- announcements */

  announcements: (communityId: string, eventId: string) =>
    call<{ announcements: Announcement[] }>(
      communityId,
      `/v1/manage/events/${eventId}/announcements`,
    ),

  createAnnouncement: (communityId: string, eventId: string, body: unknown) =>
    call<{ announcement: Announcement }>(
      communityId,
      `/v1/manage/events/${eventId}/announcements`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  updateAnnouncement: (communityId: string, announcementId: string, body: unknown) =>
    call<{ announcement: Announcement }>(
      communityId,
      `/v1/manage/announcements/${announcementId}`,
      {
        method: "PATCH",
        body: JSON.stringify(body),
      },
    ),

  deleteAnnouncement: (communityId: string, announcementId: string) =>
    call<{ ok: true }>(communityId, `/v1/manage/announcements/${announcementId}`, {
      method: "DELETE",
    }),

  publishAnnouncement: (communityId: string, announcementId: string) =>
    call<{ announcement: Announcement }>(
      communityId,
      `/v1/manage/announcements/${announcementId}/publish`,
      { method: "POST" },
    ),

  unpublishAnnouncement: (communityId: string, announcementId: string) =>
    call<{ announcement: Announcement }>(
      communityId,
      `/v1/manage/announcements/${announcementId}/unpublish`,
      { method: "POST" },
    ),

  /* ------------------------------------------------------------------ images */

  /** The poster and every update's photographs, in one call — see `routes/media.ts`. */
  eventMedia: (communityId: string, eventId: string) =>
    call<EventMedia>(communityId, `/v1/manage/events/${eventId}/media`),

  /** Posters accumulate. Removing one goes through `removeImage`, like any other attachment. */
  uploadPoster: (communityId: string, eventId: string, body: FormData) =>
    upload<{ poster: AttachmentImage }>(communityId, `/v1/manage/events/${eventId}/posters`, body),

  uploadAnnouncementImage: (communityId: string, announcementId: string, body: FormData) =>
    upload<{ image: AttachmentImage }>(
      communityId,
      `/v1/manage/announcements/${announcementId}/images`,
      body,
    ),

  /**
   * A logo, a favicon, a banner, a sponsor's mark — one endpoint for all four.
   *
   * The response carries `src`, which is what goes in the branding field. Uploading does not make
   * it the logo; saving the branding does. Anything uploaded and then not used is left where it is
   * and shows up in `brandImages` — see the pruning note in `routes/settings.ts`.
   */
  uploadBrandImage: (communityId: string, body: FormData) =>
    upload<{ image: AttachmentImage; src: string }>(
      communityId,
      "/v1/manage/community/images",
      body,
    ),

  /** Everything this community has ever uploaded for itself, in use or not. */
  brandImages: (communityId: string) =>
    call<{ images: BrandImage[] }>(communityId, "/v1/manage/community/images"),

  describeImage: (communityId: string, attachmentId: string, altText: string | null) =>
    call<{ image: AttachmentImage }>(communityId, `/v1/manage/attachments/${attachmentId}`, {
      method: "PATCH",
      body: JSON.stringify({ altText }),
    }),

  removeImage: (communityId: string, attachmentId: string) =>
    call<{ ok: true }>(communityId, `/v1/manage/attachments/${attachmentId}`, {
      method: "DELETE",
    }),

  /* ----------------------------------------------------------- notifications */

  notifications: (communityId: string, filter: { unreadOnly?: boolean; eventId?: string } = {}) => {
    const query = new URLSearchParams();
    if (filter.unreadOnly) query.set("unreadOnly", "true");
    if (filter.eventId) query.set("eventId", filter.eventId);
    const suffix = query.toString();
    return call<{ notifications: NotificationRow[] }>(
      communityId,
      `/v1/manage/notifications${suffix ? `?${suffix}` : ""}`,
    );
  },

  markNotificationRead: (communityId: string, notificationId: string) =>
    call<{ ok: true }>(communityId, `/v1/manage/notifications/${notificationId}/read`, {
      method: "POST",
    }),

  markAllNotificationsRead: (communityId: string) =>
    call<{ read: number }>(communityId, "/v1/manage/notifications/read-all", { method: "POST" }),

  /** Re-run the sweep for one event. Idempotent, so pressing it twice changes nothing. */
  refreshNotifications: (communityId: string, eventId: string) =>
    call<{ findings: number; notifications: number }>(
      communityId,
      `/v1/manage/events/${eventId}/notifications/refresh`,
      { method: "POST" },
    ),

  /* ---------------------------------------------------------------- analytics */

  analytics: (communityId: string, eventId: string) =>
    call<EventAnalytics>(communityId, `/v1/manage/events/${eventId}/analytics`),

  analyticsComparison: (communityId: string) =>
    call<EventComparison>(communityId, "/v1/manage/analytics/comparison"),

  /* ------------------------------------------------------------------ exports */

  exports: (communityId: string) =>
    call<{ exports: ExportJobRow[] }>(communityId, "/v1/manage/exports"),

  /**
   * Generate a report and hand the browser the file.
   *
   * The response is `text/csv` with a `Content-Disposition`, not JSON, so it goes around `call`
   * rather than through it: the body is read as a blob and saved through an object URL.
   *
   * The URL is revoked on a later task rather than in a `finally` beside the click. An un-revoked
   * object URL keeps the whole file alive for the life of the tab — a treasurer's tab is open all
   * weekend — but revoking it in the same tick as the click is worse: Firefox and Safari start
   * reading the blob *after* the handler returns, so a synchronous revoke races the download and
   * the file silently never appears. A timeout is long enough to lose that race in every browser
   * and short enough that the memory is not held for the session.
   *
   * The filename and the row count arrive in headers, which a cross-origin response only lets
   * JavaScript read when the server names them in `Access-Control-Expose-Headers` — see `cors()`.
   */
  createExport: async (
    communityId: string,
    eventId: string,
    body: ExportRequest,
  ): Promise<{ filename: string; rowCount: number; jobId: string }> => {
    const response = await raw(communityId, `/v1/manage/events/${eventId}/exports`, {
      method: "POST",
      body: JSON.stringify({ ...body, format: "csv" }),
    });

    const disposition = response.headers.get("content-disposition") ?? "";
    const filename =
      /filename="?([^"]+)"?/.exec(disposition)?.[1] ?? `${body.type.replace(/_/g, "-")}.csv`;

    // `type` is restated on the blob rather than inherited from the response, because a blob with
    // no type downloads as `application/octet-stream` and a phone then offers no app to open it.
    download(new Blob([await response.blob()], { type: "text/csv;charset=utf-8" }), filename);

    return {
      filename,
      rowCount: Number(response.headers.get("x-powwow-export-rows") ?? 0),
      jobId: response.headers.get("x-powwow-export-job") ?? "",
    };
  },

  /**
   * The same report, as rows for the browser to print.
   *
   * A separate call rather than a flag on `createExport`, because the two return different kinds
   * of thing — one performs a download and reports what it did, the other hands back a document to
   * lay out — and a function whose return type depends on an argument is one every caller has to
   * narrow. The *server* treats them as one path: same queries, same permission check, same
   * `export_jobs` row, so a printed participant list is on the record exactly like a CSV of it.
   *
   * `format: "print"` rather than `"pdf"`, since D97: a PDF is a document the server renders and
   * this is the screen printing what is in front of it. The distinction is not pedantic — it is
   * what the export history shows, and "printed at the desk" and "downloaded and emailed onward"
   * are different acts to be able to tell apart afterwards.
   */
  createExportReport: (communityId: string, eventId: string, body: ExportRequest) =>
    call<ExportReport>(communityId, `/v1/manage/events/${eventId}/exports`, {
      method: "POST",
      body: JSON.stringify({ ...body, format: "print" }),
    }),

  /**
   * The same report again, as a PDF the server rendered.
   *
   * Bytes, not JSON, so it goes around `call` exactly as the CSV does — and for the same reason the
   * CSV does it this way: the filename and the row count arrive in headers a cross-origin response
   * only exposes because the API names them (see `cors()`).
   *
   * Rendered on the server rather than assembled here (D97). A PDF written in the browser would
   * mean shipping a typesetter and a Unicode font into an app whose users are on venue wifi, and
   * the reason a PDF is wanted at all is that it is the copy that gets emailed onward — it has to
   * be right on a machine nobody here has seen.
   */
  createExportPdf: async (
    communityId: string,
    eventId: string,
    body: ExportRequest,
  ): Promise<{ filename: string; rowCount: number; jobId: string }> => {
    const response = await raw(communityId, `/v1/manage/events/${eventId}/exports`, {
      method: "POST",
      body: JSON.stringify({ ...body, format: "pdf" }),
    });

    const disposition = response.headers.get("content-disposition") ?? "";
    const filename =
      /filename="?([^"]+)"?/.exec(disposition)?.[1] ?? `${body.type.replace(/_/g, "-")}.pdf`;

    download(new Blob([await response.blob()], { type: "application/pdf" }), filename);

    return {
      filename,
      rowCount: Number(response.headers.get("x-powwow-export-rows") ?? 0),
      jobId: response.headers.get("x-powwow-export-job") ?? "",
    };
  },

  /**
   * The same report again, as a formatted spreadsheet built here.
   *
   * The server hands back rows and a title block; `toWorkbook` turns them into an `.xlsx` on this
   * machine, because the API Worker has 10 ms of CPU and no business assembling a zip (D12). The
   * request still names its format, so the `export_jobs` row says a workbook was taken rather than
   * a CSV — "who took a copy, and in what form" is the whole point of that record (§37).
   */
  createExportWorkbook: async (
    communityId: string,
    eventId: string,
    body: ExportRequest,
  ): Promise<{ filename: string; rowCount: number; jobId: string }> => {
    const report = await call<ExportReport>(communityId, `/v1/manage/events/${eventId}/exports`, {
      method: "POST",
      body: JSON.stringify({ ...body, format: "xlsx" }),
    });

    download(
      toWorkbook(report.columns, report.rows, {
        heading: body.includeHeader === false ? undefined : report.heading,
        sheetName: report.heading?.title,
      }),
      report.filename.endsWith(".xlsx") ? report.filename : `${report.filename}.xlsx`,
    );

    return { filename: report.filename, rowCount: report.rowCount, jobId: report.jobId };
  },

  /* ------------------------------------------------------------------ surveys */

  surveys: (communityId: string, eventId: string) =>
    call<{ surveys: SurveyRow[] }>(communityId, `/v1/manage/events/${eventId}/surveys`),

  createSurvey: (communityId: string, eventId: string, body: unknown) =>
    call<{ survey: SurveyRow }>(communityId, `/v1/manage/events/${eventId}/surveys`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updateSurvey: (communityId: string, surveyId: string, body: unknown) =>
    call<{ survey: SurveyRow }>(communityId, `/v1/manage/surveys/${surveyId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  archiveSurvey: (communityId: string, surveyId: string) =>
    call<{ ok: true }>(communityId, `/v1/manage/surveys/${surveyId}`, { method: "DELETE" }),

  surveyResults: (communityId: string, surveyId: string) =>
    call<SurveyResults>(communityId, `/v1/manage/surveys/${surveyId}/results`),

  /* -------------------------------------------------------------------- audit */

  audit: (communityId: string, query: AuditQuery = {}) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== "") params.set(key, String(value));
    }
    const suffix = params.toString();
    return call<{ entries: AuditEntryRow[]; nextCursor: string | null }>(
      communityId,
      `/v1/manage/audit${suffix ? `?${suffix}` : ""}`,
    );
  },

  /* ----------------------------------------------------------------- QR codes */

  qrCodes: (communityId: string, eventId: string) =>
    call<{ targets: QrTarget[] }>(communityId, `/v1/manage/events/${eventId}/qr-codes`),

  /* ------------------------------------------------------------------- phases */

  phases: (communityId: string, competitionId: string) =>
    call<{ phases: Phase[] }>(communityId, `/v1/manage/competitions/${competitionId}/phases`),

  createPhase: (
    communityId: string,
    competitionId: string,
    body: { name: string; sortOrder: number; advancementRule?: AdvancementRule },
  ) =>
    call<{ phase: Phase }>(communityId, `/v1/manage/competitions/${competitionId}/phases`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** `advancementRule: null` clears the rule; leaving the field out keeps it. */
  updatePhase: (
    communityId: string,
    phaseId: string,
    body: { name?: string; sortOrder?: number; advancementRule?: AdvancementRule | null },
  ) =>
    call<{ phase: Phase }>(communityId, `/v1/manage/phases/${phaseId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  deletePhase: (communityId: string, phaseId: string) =>
    call<{ ok: true }>(communityId, `/v1/manage/phases/${phaseId}`, { method: "DELETE" }),

  /* -------------------------------------------------- tabulation adjustments */

  adjustments: (communityId: string, categoryId: string, phaseId?: string) =>
    call<{ adjustments: Adjustment[] }>(
      communityId,
      `/v1/ops/categories/${categoryId}/adjustments${phaseId ? `?phaseId=${phaseId}` : ""}`,
    ),

  addAdjustment: (
    communityId: string,
    categoryId: string,
    body: {
      registrationEntryId: string;
      phaseId?: string;
      kind: AdjustmentKind;
      label: string;
      points: number;
      reason: string;
    },
  ) =>
    call<{ adjustment: Adjustment }>(communityId, `/v1/ops/categories/${categoryId}/adjustments`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** A reason is required to take one away as well as to add one. */
  removeAdjustment: (communityId: string, adjustmentId: string, reason: string) =>
    call<{ ok: true }>(communityId, `/v1/ops/adjustments/${adjustmentId}`, {
      method: "DELETE",
      body: JSON.stringify({ reason }),
    }),

  disqualifyEntry: (communityId: string, entryId: string, reason: string) =>
    call<{ ok: true }>(communityId, `/v1/ops/entries/${entryId}/disqualify`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  reinstateEntry: (communityId: string, entryId: string, reason: string) =>
    call<{ ok: true }>(communityId, `/v1/ops/entries/${entryId}/reinstate`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  /** Read-only: the rule is evaluated and explained, never written (see the route's comment). */
  advancement: (communityId: string, categoryId: string, phaseId: string) =>
    call<{ phase: Phase; resultSetId: string; outcome: AdvancementOutcome }>(
      communityId,
      `/v1/ops/categories/${categoryId}/advancement?phaseId=${phaseId}`,
      { method: "POST" },
    ),

  overrideRank: (communityId: string, resultId: string, rank: number, reason: string) =>
    call<{ ok: true }>(communityId, `/v1/ops/results/${resultId}/override`, {
      method: "POST",
      body: JSON.stringify({ rank, reason }),
    }),

  /* -------------------------------------------------------------------- self */

  updateProfile: (body: {
    name?: string;
    preferredName?: string | null;
    phone?: string | null;
    locale?: string | null;
  }) =>
    call<{ user: User }>(undefined, "/v1/auth/profile", {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
};

/**
 * Money, as a treasurer reads it.
 *
 * Re-exported rather than written here, because the server now formats the same amounts onto the
 * signature sheet a dancer signs for — and the screen and the sheet disagreeing about a currency or
 * a separator is the kind of difference nobody notices until somebody is counting cash against a
 * printout. One implementation, in `@powwow/domain/money`; this stays as the name every screen in
 * this app already imports.
 */
export { formatMoney } from "@powwow/domain/money";

/**
 * A moment, in the words a person would use.
 *
 * "expires in 6 days", "last seen 3 hours ago" — never a raw timestamp and never a bare date for
 * something whose whole meaning is how far away it is. A volunteer reading a members list should
 * not have to do arithmetic against today's date to know whether an invitation is still live.
 */
export function formatRelative(instant: number, now = Date.now()): string {
  const seconds = Math.round((instant - now) / 1000);
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ["year", 31_536_000],
    ["month", 2_592_000],
    ["week", 604_800],
    ["day", 86_400],
    ["hour", 3600],
    ["minute", 60],
  ];
  const formatter = new Intl.RelativeTimeFormat("en-CA", { numeric: "auto" });
  for (const [unit, size] of units) {
    if (Math.abs(seconds) >= size) return formatter.format(Math.round(seconds / size), unit);
  }
  return "just now";
}

/** A date and time, in the reader's own zone. Used where the exact moment matters. */
export function formatMoment(instant: number): string {
  return new Intl.DateTimeFormat("en-CA", { dateStyle: "medium", timeStyle: "short" }).format(
    new Date(instant),
  );
}

/** Turn a failure into something a person can act on. */
export function describeError(cause: unknown): string {
  if (cause instanceof ApiRequestError) return cause.message;
  if (cause instanceof Error) return `Couldn't reach the server. ${cause.message}`;
  return "Something went wrong.";
}
